import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Inbox, Clock, Receipt, Calendar as CalIcon, BookOpenCheck, CheckCircle2, XCircle, Users, Eye } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const fmtMoney = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (s) => s ? new Date(s).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" }) : "";

const SEEN_KEY = "ndis_inbox_seen_ids";

export default function InboxPage() {
  const { clients } = useAuth();
  const [inbox, setInbox] = useState({ pending_timesheets: [], pending_invoices: [], upcoming_shifts: [], recent_decisions: [], engagement_count: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all"); // "all" | client_id
  const [seenIds, setSeenIds] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); }
    catch { return new Set(); }
  });

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/me/inbox"); setInbox(data); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Filter rows by the active tab (client_id) — all rows pass on "all".
  const filterByClient = (rows) => activeTab === "all" ? rows : rows.filter((r) => r.client_id === activeTab);

  const pts = filterByClient(inbox.pending_timesheets);
  const pis = filterByClient(inbox.pending_invoices);
  const us  = filterByClient(inbox.upcoming_shifts);
  const rd  = filterByClient(inbox.recent_decisions);
  const ec  = inbox.engagement_count;
  const total = pts.length + pis.length + us.length;

  // Per-client unseen counts (across pending + upcoming, excluding rows already locally seen).
  const tabCounts = useMemo(() => {
    const all = [...inbox.pending_timesheets, ...inbox.pending_invoices, ...inbox.upcoming_shifts];
    const out = { all: 0 };
    inbox.engagement_count > 0 && (clients || []).forEach((c) => { out[c.client_id] = 0; });
    all.forEach((r) => {
      if (seenIds.has(r.id)) return;
      out.all += 1;
      if (r.client_id in out) out[r.client_id] += 1;
    });
    return out;
  }, [inbox, clients, seenIds]);

  const markAllSeen = () => {
    const all = [...inbox.pending_timesheets, ...inbox.pending_invoices,
                 ...inbox.upcoming_shifts, ...inbox.recent_decisions];
    const ids = all.map((r) => r.id);
    const merged = new Set([...seenIds, ...ids]);
    setSeenIds(merged);
    localStorage.setItem(SEEN_KEY, JSON.stringify([...merged].slice(-500))); // cap for safety
    toast.success(`${ids.length} item${ids.length === 1 ? "" : "s"} marked seen`);
  };

  return (
    <div className="space-y-6" data-testid="inbox-page">
      <header className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-4xl font-display font-black tracking-tight flex items-center gap-3">
            <Inbox className="w-8 h-8 text-worker" /> Inbox
            {ec > 0 && (
              <span className="rounded-full bg-worker/15 text-worker px-3 py-1 text-sm font-bold flex items-center gap-1.5">
                <Users className="w-3.5 h-3.5" /> {ec} client{ec === 1 ? "" : "s"}
              </span>
            )}
          </h1>
          <p className="text-muted-foreground">Everything that needs your attention across all your clients — at a glance.</p>
        </div>
        <div className="flex items-center gap-2">
          {tabCounts.all > 0 && (
            <Button variant="outline" onClick={markAllSeen} className="rounded-full font-bold gap-2 min-h-[44px]" data-testid="mark-all-seen-btn">
              <Eye className="w-4 h-4" /> Mark all seen
            </Button>
          )}
          {!loading && total === 0 && rd.length === 0 && (
            <span className="rounded-full px-3 py-1 bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 text-xs font-bold uppercase tracking-wider flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> All caught up
            </span>
          )}
        </div>
      </header>

      {/* Per-client tabs — only render for power workers with 2+ engagements */}
      {ec >= 2 && (
        <div className="flex flex-wrap gap-2 border-b" data-testid="inbox-tabs">
          <TabBtn active={activeTab === "all"} onClick={() => setActiveTab("all")} testId="inbox-tab-all"
                  badge={tabCounts.all}>All clients</TabBtn>
          {(clients || []).map((c) => (
            <TabBtn key={c.client_id}
                    active={activeTab === c.client_id}
                    onClick={() => setActiveTab(c.client_id)}
                    testId={`inbox-tab-${c.client_id}`}
                    badge={tabCounts[c.client_id] || 0}>
              {c.client_name || c.client_email}
            </TabBtn>
          ))}
        </div>
      )}

      {/* Pending submissions awaiting client approval */}
      <Section icon={Clock} title="Awaiting client approval" count={pts.length + pis.length} accent="amber-500">
        {pts.map((t) => (
          <Row key={`t-${t.id}`} testId={`inbox-ts-${t.id}`}
               icon={BookOpenCheck} clientName={t.client_name}
               title={`${fmtDate(t.date)} · ${t.start_time}–${t.end_time}`}
               subtitle={`${t.rate_type || ""} · ${t.ndis_code || ""}`}
               right={<><div className="font-display font-black text-lg">{fmtMoney(t.total_amount)}</div><Pill color="amber">PENDING</Pill></>}
               linkTo="/timesheets" />
        ))}
        {pis.map((i) => (
          <Row key={`i-${i.id}`} testId={`inbox-inv-${i.id}`}
               icon={Receipt} clientName={i.client_name}
               title={`Invoice ${i.invoice_number || ""}`}
               subtitle={`Generated ${fmtDate(i.created_at)}`}
               right={<><div className="font-display font-black text-lg">{fmtMoney(i.total_amount)}</div><Pill color="amber">PENDING</Pill></>}
               linkTo="/invoices" />
        ))}
        {pts.length + pis.length === 0 && <Empty>Nothing awaiting approval. Nice work.</Empty>}
      </Section>

      {/* Upcoming shifts (next 14 days) */}
      <Section icon={CalIcon} title="Upcoming shifts (next 14 days)" count={us.length} accent="client">
        {us.length === 0 && <Empty>No shifts on the roster.</Empty>}
        {us.map((s) => (
          <Row key={`s-${s.id}`} testId={`inbox-shift-${s.id}`}
               icon={CalIcon} clientName={s.client_name}
               title={`${fmtDate(s.date)} · ${s.start_time}–${s.end_time}`}
               subtitle={s.notes || s.location || "—"}
               right={<Pill color="emerald">{s.status || "scheduled"}</Pill>}
               linkTo="/roster" />
        ))}
      </Section>

      {/* Recent decisions (approved / rejected) */}
      <Section icon={CheckCircle2} title="Recent decisions" count={rd.length} accent="success">
        {rd.length === 0 && <Empty>No recent activity.</Empty>}
        {rd.map((d) => {
          const approved = d.status === "approved";
          return (
            <Row key={`d-${d.kind}-${d.id}`} testId={`inbox-dec-${d.kind}-${d.id}`}
                 icon={approved ? CheckCircle2 : XCircle}
                 iconColor={approved ? "text-emerald-500" : "text-destructive"}
                 clientName={d.client_name}
                 title={`${d.kind === "timesheet" ? "Timesheet" : "Invoice"} ${d.kind === "invoice" ? (d.invoice_number || "") : fmtDate(d.date)}`}
                 subtitle={`${approved ? "Approved" : "Rejected"} · ${fmtDate(d.created_at)}`}
                 right={<Pill color={approved ? "emerald" : "destructive"}>{approved ? "APPROVED" : "REJECTED"}</Pill>}
                 linkTo={d.kind === "invoice" ? "/invoices" : "/timesheets"} />
          );
        })}
      </Section>
    </div>
  );
}

function Section({ icon: Icon, title, count, children, accent = "client" }) {
  return (
    <div className="space-y-3">
      <h2 className="font-display font-extrabold text-xl flex items-center gap-2">
        <Icon className={`w-5 h-5`} style={{ color: `hsl(var(--${accent}, 173 53% 48%))` }} />
        {title}
        <span className="rounded-full bg-muted px-2.5 py-0.5 text-xs font-bold">{count}</span>
      </h2>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ icon: Icon, iconColor = "text-foreground/60", clientName, title, subtitle, right, linkTo, testId }) {
  const inner = (
    <Card className="rounded-2xl p-4 border-2 hover:shadow-md transition flex items-center gap-3" data-testid={testId}>
      <Icon className={`w-5 h-5 flex-shrink-0 ${iconColor}`} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <span className="font-bold truncate">{title}</span>
          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wider font-bold whitespace-nowrap">
            {clientName}
          </span>
        </div>
        {subtitle && <div className="text-xs text-muted-foreground truncate">{subtitle}</div>}
      </div>
      <div className="text-right space-y-1 flex flex-col items-end whitespace-nowrap">{right}</div>
    </Card>
  );
  return linkTo ? <Link to={linkTo}>{inner}</Link> : inner;
}

function TabBtn({ active, onClick, children, badge, testId }) {
  return (
    <button onClick={onClick} data-testid={testId}
            className={`px-4 py-2.5 text-sm font-bold flex items-center gap-2 transition border-b-2 -mb-px ${
              active ? "text-foreground border-coral" : "text-muted-foreground border-transparent hover:text-foreground"
            }`}>
      {children}
      {badge > 0 && (
        <span className="rounded-full bg-coral text-white px-2 py-0.5 text-[10px] font-extrabold">{badge}</span>
      )}
    </button>
  );
}

function Pill({ children, color }) {
  const map = {
    amber:       "bg-amber-500/20 text-amber-700 dark:text-amber-200",
    emerald:     "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300",
    destructive: "bg-destructive/20 text-destructive",
    sky:         "bg-sky-500/20 text-sky-700 dark:text-sky-300",
  };
  return <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${map[color] || map.sky}`}>{children}</span>;
}

function Empty({ children }) {
  return <Card className="rounded-2xl p-6 border-2 border-dashed text-center text-muted-foreground text-sm">{children}</Card>;
}
